import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';
import { getEligibleGiftAccessories, selectFreeGift } from '../api/freeGift.api';
import { toast } from './Toast';

const formatKES = (n) =>
    'KES ' + Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

function resolveImage(path) {
    if (!path) return null;
    return path.startsWith('http') ? path : `${API_URL}/${path}`;
}

const TYPE_LABELS = {
    charger: 'Chargers',
    cable: 'Cables',
    cover: 'Cases',
    audio: 'Audio',
    screen_protector: 'Screen Protectors',
    other: 'Other',
};

/**
 * Modal for selecting a free gift accessory.
 * Props:
 *   currentSlab    – { maxFreeValue, maxQuantity, slabIndex }
 *   selectedGifts  – currently selected gift variantIds (array of strings)
 *   onClose        – close callback
 *   onGiftSelected – called after a gift is successfully selected (triggers cart refresh)
 */
export default function GiftSelectionModal({ currentSlab, selectedGifts = [], onClose, onGiftSelected }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');
    const [selecting, setSelecting] = useState({});

    const selectedVariantIds = new Set((selectedGifts || []).map((g) => g.variantId?.toString()));

    useEffect(() => {
        if (!currentSlab) return;
        setLoading(true);
        getEligibleGiftAccessories(currentSlab.maxFreeValue)
            .then((res) => setItems(res?.data?.isOk ? (res.data.data || []) : []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [currentSlab?.maxFreeValue]);

    const tabs = useMemo(() => {
        const types = [...new Set((items || []).map((i) => i.accessoryType || 'other'))];
        return ['all', ...types];
    }, [items]);

    const visibleItems = useMemo(() => {
        if (activeTab === 'all') return items;
        return items.filter((i) => (i.accessoryType || 'other') === activeTab);
    }, [items, activeTab]);

    const handleSelect = async (item) => {
        if (selecting[item.variantId]) return;
        setSelecting((prev) => ({ ...prev, [item.variantId]: true }));
        try {
            await selectFreeGift(item.variantId);
            toast.success(`${item.name} added as your free gift!`);
            onGiftSelected?.();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to select gift');
        } finally {
            setSelecting((prev) => ({ ...prev, [item.variantId]: false }));
        }
    };

    if (!currentSlab) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border">
                    <div>
                        <div className="flex items-center gap-2">
                            <i className="ri-gift-fill text-green-600 text-xl"></i>
                            <h2 className="text-base font-bold text-foreground">Choose Your Free Gift</h2>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Choose any accessory worth up to <strong className="text-green-700">{formatKES(currentSlab.maxFreeValue)}</strong> — completely free!
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground transition-colors"
                    >
                        <i className="ri-close-line text-lg"></i>
                    </button>
                </div>

                {/* Tabs */}
                {tabs.length > 2 && (
                    <div className="flex gap-2 px-6 pt-4 overflow-x-auto flex-shrink-0">
                        {tabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                                    activeTab === tab
                                        ? 'bg-primary text-white'
                                        : 'bg-muted text-muted-foreground hover:bg-primary/10'
                                }`}
                            >
                                {tab === 'all' ? 'All' : TYPE_LABELS[tab] || tab}
                            </button>
                        ))}
                    </div>
                )}

                {/* Items */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin"></div>
                        </div>
                    ) : visibleItems.length === 0 ? (
                        <div className="text-center py-10 text-muted-foreground">
                            <i className="ri-gift-line text-4xl block mb-2 opacity-30"></i>
                            <p className="text-sm">No eligible accessories in this category</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {visibleItems.map((item) => {
                                const imgSrc = resolveImage(item.image);
                                const isAlreadySelected = selectedVariantIds.has(item.variantId?.toString());
                                const isSelecting = selecting[item.variantId];

                                return (
                                    <div
                                        key={item.variantId || item._id}
                                        className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                                            isAlreadySelected
                                                ? 'border-green-400 bg-green-50'
                                                : 'border-border hover:border-primary/40'
                                        }`}
                                    >
                                        {/* Image */}
                                        <div className="w-14 h-14 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
                                            {imgSrc ? (
                                                <img src={imgSrc} alt={item.name} className="w-full h-full object-contain p-1" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <i className="ri-image-line text-muted-foreground/40 text-xl"></i>
                                                </div>
                                            )}
                                        </div>

                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <Link
                                                to={`/product/${item.slug}`}
                                                onClick={onClose}
                                                className="text-sm font-semibold text-foreground hover:text-primary line-clamp-1"
                                            >
                                                {item.name}
                                            </Link>
                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                <span className="text-[10px] bg-green-100 text-green-700 font-bold px-1.5 py-0.5 rounded">FREE</span>
                                                <span className="text-xs text-muted-foreground line-through">{formatKES(item.price)}</span>
                                            </div>
                                        </div>

                                        {/* Action */}
                                        <button
                                            onClick={() => !isAlreadySelected && handleSelect(item)}
                                            disabled={isSelecting || isAlreadySelected}
                                            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                                isAlreadySelected
                                                    ? 'bg-green-100 text-green-700 cursor-default'
                                                    : 'bg-primary text-white hover:brightness-110 disabled:opacity-50'
                                            }`}
                                        >
                                            {isSelecting ? (
                                                <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block"></span>
                                            ) : isAlreadySelected ? (
                                                <span className="flex items-center gap-1"><i className="ri-check-line"></i> Selected</span>
                                            ) : (
                                                <span className="flex items-center gap-1"><i className="ri-gift-line"></i> Select</span>
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 pb-5 pt-3 border-t border-border flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="w-full py-2.5 border border-border rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
                    >
                        Continue Shopping
                    </button>
                </div>
            </div>
        </div>
    );
}
